import { UploadOutlined } from '@ant-design/icons';
import {
  describeError,
  dirhams,
  PhotoField,
  parseBalance,
  present,
} from '@horof/shared';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Input,
  Modal,
  Select,
  Tag,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useClient, useQuery } from 'urql';
import {
  BANK_ERRORS,
  bankContext,
  ImportStatementDocument,
  LastStatementDocument,
  linesCount,
} from './bank-data';
import {
  guessMapping,
  type Mapping,
  readLines,
  splitRows,
  UNDATED,
} from './bank-import';
import { useLists } from './data';

type Check = {
  /** What was checked: a change in the form asks for a new check. */
  key: string;
  lineCount: number;
  total: string;
  duplicates: number[];
};

const COLUMN_FIELDS: { key: keyof Omit<Mapping, 'header'>; label: string }[] = [
  { key: 'date', label: 'التاريخ' },
  { key: 'label', label: 'البيان' },
  { key: 'amount', label: 'المبلغ (بإشارته)' },
  { key: 'debit', label: 'مدين (خروج)' },
  { key: 'credit', label: 'دائن (دخول)' },
  { key: 'balance', label: 'الرصيد (اختياري)' },
  { key: 'reference', label: 'المرجع (اختياري)' },
];

/**
 * «استيراد كشف»: the bank's lines pasted from Excel or read from a CSV
 * file, the columns guessed then adjustable, every line shown as read.
 * «تحقق» asks arqam (dry run: totals, period, duplicates); «استيراد» saves
 * and opens the statement for matching.
 */
export function BankImport({ onClose }: { onClose: () => void }) {
  const client = useClient();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [{ data: lists }] = useLists();
  const banks = present(lists?.financialAccounts?.nodes).filter(
    (a) => a.type === 'bank_account',
  );
  const [accountId, setAccountId] = useState<string | null>(null);
  const [period, setPeriod] = useState<[string, string] | null>(null);
  const [opening, setOpening] = useState('');
  const [closing, setClosing] = useState('');
  const [photoId, setPhotoId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [text, setText] = useState('');
  const [mapping, setMapping] = useState<Mapping | null>(null);
  // Rows ticked or unticked by hand; the others follow `undated`.
  const [toggled, setToggled] = useState<Map<number, boolean>>(new Map());
  const [check, setCheck] = useState<Check | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // One bank account: chosen already.
  useEffect(() => {
    if (!accountId && banks.length === 1) setAccountId(banks[0].id);
  }, [accountId, banks]);

  // The next statement starts after the last one, from its closing balance.
  const [{ data: last }] = useQuery({
    query: LastStatementDocument,
    variables: { accountId: accountId ?? '' },
    pause: !accountId,
    context: useMemo(() => bankContext, []),
  });
  const previous = last?.bankStatements?.nodes[0] ?? null;
  useEffect(() => {
    if (!previous) return;
    setOpening((o) => o || String(Number(previous.closingBalance)));
  }, [previous]);

  const rows = useMemo(() => splitRows(text), [text]);
  useEffect(() => {
    setMapping(rows.length ? guessMapping(rows) : null);
    setToggled(new Map());
  }, [rows]);
  const read = useMemo(
    () => (mapping ? readLines(rows, mapping) : []),
    [rows, mapping],
  );
  // A row without a date (total, «solde initial») starts excluded.
  const isOut = (r: (typeof read)[number]) =>
    toggled.get(r.row) ?? r.problem === UNDATED;
  const kept = read.filter((r) => !isOut(r));
  const unreadable = kept.filter((r) => r.problem);
  const sent = kept.flatMap((r) => (r.line ? [r] : []));

  // A period from the lines when none is chosen: the day after the last
  // statement when the lines follow it, else their first month; up to the
  // end of the last line's month.
  useEffect(() => {
    if (period || !sent.length) return;
    const dates = sent.map((r) => r.line?.date ?? '').sort();
    const first = dates[0];
    const last = dates.at(-1) ?? first;
    const start =
      previous && first > previous.periodEnd
        ? dayjs(previous.periodEnd).add(1, 'day').format('YYYY-MM-DD')
        : dayjs(first).startOf('month').format('YYYY-MM-DD');
    const end = dayjs(last > start ? last : start)
      .endOf('month')
      .format('YYYY-MM-DD');
    setPeriod([start, end]);
  }, [period, sent, previous]);

  const openingValue = parseBalance(opening);
  const closingValue = parseBalance(closing);
  const ready =
    accountId &&
    period &&
    openingValue &&
    closingValue &&
    opening.trim() &&
    closing.trim() &&
    sent.length > 0 &&
    unreadable.length === 0;
  const variables = ready
    ? {
        accountId,
        periodStart: period[0],
        periodEnd: period[1],
        openingBalance: openingValue,
        closingBalance: closingValue,
        lines: sent.map((r) => r.line),
        documentPhotoId: photoId,
      }
    : null;
  const key = JSON.stringify(variables);
  const checked = check?.key === key ? check : null;
  // Positions sent (from 1) → rows of the pasted text.
  const duplicateRows = new Set(
    (checked?.duplicates ?? []).map((n) => sent[n - 1]?.row),
  );

  async function run(dryRun: boolean) {
    if (!variables) return;
    setBusy(true);
    setProblem(null);
    const r = await client
      .mutation(ImportStatementDocument, { ...variables, dryRun }, bankContext)
      .toPromise();
    setBusy(false);
    if (r.error) {
      setCheck(null);
      setProblem(describeError(r.error, 'تعذّر الاستيراد.', BANK_ERRORS));
      return;
    }
    const result = r.data?.appImportBankStatement?.result;
    if (!result) return;
    if (dryRun) {
      setCheck({
        key,
        lineCount: result.lineCount,
        total: result.total,
        duplicates: present(result.duplicateLines),
      });
      return;
    }
    if (result.statementId) navigate(`/bank/${result.statementId}`);
  }

  async function readFile(file: File) {
    const buffer = await file.arrayBuffer();
    let content = new TextDecoder('utf-8').decode(buffer);
    // Old Windows exports (French accents).
    if (content.includes('�')) {
      content = new TextDecoder('windows-1252').decode(buffer);
    }
    setText(content);
  }

  const columnOptions = (mapping ? (rows[0] ?? []) : []).map((cell, i) => ({
    value: i,
    label: mapping?.header ? cell || `العمود ${i + 1}` : `العمود ${i + 1}`,
  }));

  return (
    <Modal
      open
      width={1040}
      title="استيراد كشف بنكي"
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          رجوع
        </Button>,
        <Button
          key="check"
          disabled={!ready || uploading}
          loading={busy && !checked}
          onClick={() => run(true)}
        >
          تحقق
        </Button>,
        <Button
          key="import"
          type="primary"
          disabled={!checked || checked.duplicates.length > 0 || uploading}
          loading={busy && !!checked}
          onClick={() => run(false)}
        >
          استيراد {sent.length > 0 && `(${linesCount(sent.length)})`}
        </Button>,
      ]}
    >
      <div className="desk-import">
        <div className="desk-import-head">
          <div>
            <label htmlFor="bi-account">الحساب</label>
            <Select
              id="bi-account"
              value={accountId ?? undefined}
              onChange={setAccountId}
              placeholder="الحساب البنكي"
              options={banks.map((a) => ({ value: a.id, label: a.name }))}
            />
          </div>
          <div>
            <label htmlFor="bi-period">فترة الكشف</label>
            <DatePicker.RangePicker
              id="bi-period"
              allowClear={false}
              value={period ? [dayjs(period[0]), dayjs(period[1])] : null}
              onChange={(v) =>
                v?.[0] &&
                v[1] &&
                setPeriod([
                  v[0].format('YYYY-MM-DD'),
                  v[1].format('YYYY-MM-DD'),
                ])
              }
            />
          </div>
          <div>
            <label htmlFor="bi-opening">الرصيد الافتتاحي</label>
            <Input
              id="bi-opening"
              className="ltr-num"
              value={opening}
              onChange={(e) => setOpening(e.target.value)}
              status={opening && !openingValue ? 'error' : undefined}
            />
          </div>
          <div>
            <label htmlFor="bi-closing">الرصيد الختامي</label>
            <Input
              id="bi-closing"
              className="ltr-num"
              value={closing}
              onChange={(e) => setClosing(e.target.value)}
              status={closing && !closingValue ? 'error' : undefined}
            />
          </div>
        </div>
        {previous && (
          <div className="field-hint">
            الكشف السابق ينتهي في{' '}
            <span className="ltr-num">{previous.periodEnd}</span> برصيد{' '}
            {dirhams(previous.closingBalance)}.
          </div>
        )}

        <div className="desk-import-paste">
          <Input.TextArea
            value={text}
            onChange={(e) => setText(e.target.value)}
            autoSize={{ minRows: 4, maxRows: 8 }}
            placeholder="انسخ أسطر الكشف من Excel (مع سطر العناوين) والصقها هنا، أو اختر ملف CSV."
            className="ltr-num"
          />
          <div className="desk-import-side">
            <Button
              icon={<UploadOutlined />}
              onClick={() => fileRef.current?.click()}
            >
              ملف CSV
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.txt,text/csv,text/plain"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) readFile(file);
                e.target.value = '';
              }}
            />
            <PhotoField
              kind="RECEIPT"
              label="الكشف (PDF أو صورة)"
              hint="اختياري، يُحفظ مع الكشف."
              value={photoId}
              onChange={setPhotoId}
              onBusyChange={setUploading}
            />
          </div>
        </div>

        {mapping && (
          <div className="desk-import-map">
            <Checkbox
              checked={mapping.header}
              onChange={(e) =>
                setMapping({ ...mapping, header: e.target.checked })
              }
            >
              السطر الأول عناوين
            </Checkbox>
            {COLUMN_FIELDS.map((f) => (
              <div key={f.key}>
                <label htmlFor={`bi-${f.key}`}>{f.label}</label>
                <Select
                  id={`bi-${f.key}`}
                  size="small"
                  allowClear
                  value={mapping[f.key] >= 0 ? mapping[f.key] : undefined}
                  onChange={(v) => {
                    const next = { ...mapping, [f.key]: v ?? -1 };
                    // One signed amount, or the two columns.
                    if (f.key === 'amount' && v !== undefined) {
                      next.debit = -1;
                      next.credit = -1;
                    }
                    if (
                      (f.key === 'debit' || f.key === 'credit') &&
                      v !== undefined
                    ) {
                      next.amount = -1;
                    }
                    setMapping(next);
                  }}
                  options={columnOptions}
                  placeholder="—"
                />
              </div>
            ))}
          </div>
        )}

        {problem && <Alert type="error" showIcon message={problem} />}
        {checked && checked.duplicates.length === 0 && (
          <Alert
            type="success"
            showIcon
            message={`الكشف سليم: ${linesCount(checked.lineCount)}، مجموعها ${dirhams(checked.total)}، ويتطابق مع الرصيدين.`}
          />
        )}
        {checked && checked.duplicates.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`${linesCount(checked.duplicates.length)} مستوردة من قبل لهذا الحساب (نفس التاريخ والمبلغ والبيان).`}
            action={
              <Button
                size="small"
                onClick={() =>
                  setToggled(
                    (t) =>
                      new Map([
                        ...t,
                        ...[...duplicateRows].map((n) => [n, true] as const),
                      ]),
                  )
                }
              >
                استبعادها
              </Button>
            }
          />
        )}
        {unreadable.length > 0 && (
          <div className="field-error">
            {linesCount(unreadable.length)} غير مقروءة: صحّح الأعمدة أو استبعدها.
          </div>
        )}

        {read.length > 0 && (
          <div className="desk-import-preview">
            <table className="desk-changes">
              <thead>
                <tr>
                  <th />
                  <th>السطر</th>
                  <th>التاريخ</th>
                  <th>البيان</th>
                  <th>المبلغ</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {read.slice(0, 300).map((r) => {
                  const out = isOut(r);
                  return (
                    <tr
                      key={r.row}
                      className={
                        out
                          ? 'is-excluded'
                          : r.problem
                            ? 'is-failed'
                            : undefined
                      }
                    >
                      <td>
                        <Checkbox
                          checked={!out}
                          title="يُستورد"
                          onChange={(e) =>
                            setToggled((t) =>
                              new Map(t).set(r.row, !e.target.checked),
                            )
                          }
                        />
                      </td>
                      <td className="ltr-num">{r.row}</td>
                      <td className="ltr-num">{r.line?.date ?? '—'}</td>
                      <td>{r.line?.label ?? r.cells.join(' | ')}</td>
                      <td
                        className={`ltr-num ${r.line?.amount.startsWith('-') ? 'is-debit' : 'is-credit'}`}
                      >
                        {r.line ? dirhams(r.line.amount) : '—'}
                      </td>
                      <td>
                        {r.problem && !out && (
                          <Tag color="red">{r.problem}</Tag>
                        )}
                        {duplicateRows.has(r.row) && !out && (
                          <Tag color="orange">مستورد من قبل</Tag>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}
